import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import {
    catchError,
    Observable,
    of,
    shareReplay,
    Subject,
    switchMap,
    tap,
} from 'rxjs';
import { RouterService } from './router.service';
import { filter, map } from 'rxjs/operators';
import { AccountInfo, InteractionStatus } from '@azure/msal-browser';
import { AppConfigService } from '../../app.config';
import { Level, Role } from '../enums/auth';
import { Assignment } from '../types/requests/assignments/assignment';

@Injectable({ providedIn: 'root' })
export class AuthService implements OnDestroy {
    private readonly userKey = 'las-user';
    private authSuccess$ = new Subject<boolean>();
    /**
     * The url of the last failed login guard result.
     */
    redirectUrl = '';
    /**
     * Observable of auth results. Replays last item to all subscribers.
     */
    authed$ = this.authSuccess$.asObservable().pipe(shareReplay(1));

    constructor(
        private http: HttpClient,
        private router: RouterService,
        private msal: MsalService,
        private broadcast: MsalBroadcastService,
        private config: AppConfigService,
    ) {}

    /**
     * Gets the active account from msal.
     */
    get activeAccount(): AccountInfo | null {
        return this.msal.instance.getActiveAccount();
    }

    /**
     * Sets the active account in msal.
     * @param account the current account.
     */
    set activeAccount(account: AccountInfo | null) {
        this.msal.instance.setActiveAccount(account);
    }

    /**
     * Gets the user from localStorage if exists.
     */
    get user(): any | null {
        const userJson = localStorage.getItem(this.userKey);
        return userJson ? JSON.parse(userJson) : userJson;
    }

    /**
     * Sets the user in localStorage.
     * @param user the user.
     */
    set user(user: any) {
        const userJson = JSON.stringify(user);
        localStorage.setItem(this.userKey, userJson);
    }

    /**
     * Gets the user's lowercase role, if exists; otherwise, null.
     */
    get userRole(): string | null {
        const roles = this.activeAccount?.idTokenClaims?.roles ?? [];
        return roles.length === 0 ? null : roles[0].toLowerCase();
    }

    /**
     * Gets all accounts in msal.
     */
    get accounts() {
        return this.msal.instance.getAllAccounts();
    }

    /**
     * Attempts to acquire a token silently; on failure, triggers an msal login redirect with a url of the last failed
     * nav to a route protected by the LoginGuard. If the route is to the root or login, the dashboard will be used.
     * @param scopes an optional list of scopes to log in with. (defaults to [])
     */
    login(scopes: string[] = []) {
        this.isAuthenticatedAndAuthorized().subscribe((isAuthed) => {
            if (!isAuthed) {
                // We must log them in with interactive redirect.
                this.msal.loginRedirect({
                    redirectStartPage: this.correctRedirect(this.redirectUrl),
                    scopes: scopes,
                });
            }
        });
    }

    /**
     * Removes the las user from cache and triggers an msal logout redirect.
     */
    logout() {
        localStorage.removeItem(this.userKey);
        this.msal.logoutRedirect();
    }

    /**
     * Determines if the user can be authenticated silently; if successful, it will authorize the user through the api's
     * `Me` endpoint and return true if successful. Otherwise, false; a false value would indicate the user must use
     * an interactive method of auth.
     * @param scopes an optional list of scopes to log in with. (defaults to [])
     */
    isAuthenticatedAndAuthorized(scopes: string[] = []): Observable<boolean> {
        if (this.hasAccounts()) {
            // An active account must be set in msal or passed in the token request.
            // We're currently only supporting single accounts so this will always be the first.
            this.activeAccount ??= this.accounts[0];
            // Refreshing access tokens requires the original scope.
            // TODO: Determine if this is problematic. Usually, the scopes will be empty but, if not, what happens?
            scopes.push(this.config.getConfig().apiClientScope);
            return this.msal.initialize().pipe(
                switchMap((_) =>
                    this.msal.acquireTokenSilent({ scopes: scopes }).pipe(
                        switchMap((_) => this.authorize()),
                        tap((result) => this.handleAuth(result)),
                        catchError((error) => {
                            console.error(error);
                            // TODO: Consider if this and the one in `tap` should be consolidated in a `finalize` op.
                            //  The issue is explicitly unsubscribing from the observable causes this to run which may
                            //  cause issues with forced logouts?
                            this.handleAuth(false);
                            return of(false);
                        }),
                    ),
                ),
            );
        }
        return of(false);
    }

    /**
     * Determines if the user is already authed or can be authed silently. If not, awaits for the msal authentication
     * interaction to complete then, calls the Me api to authorize the user.
     * @see isAuthenticatedAndAuthorized
     */
    isFullyAuthed(): Observable<boolean> {
        return this.isAuthenticatedAndAuthorized().pipe(
            switchMap((result) =>
                result ? of(result) : this.interactiveAuth(),
            ),
        );
    }

    /**
     * Gets a minimal authorization from the api via Me#GetMe and loads the user's assignments in the process.
     */
    authorize(): Observable<boolean> {
        const meUrl = this.router.getApiBaseUrl('me');
        return this.http.get<any>(meUrl).pipe(
            switchMap((user) => {
                if (!user) return of(user);
                return this.getUserAssignments().pipe(
                    map((assignments) => {
                        user.assigments = assignments;
                        return user;
                    }),
                );
            }),
            tap((user) => (this.user = user)),
            map((user) => user !== null),
        );
    }

    /**
     * Determines if the user has an admin role.
     */
    isAdmin = () => this.inRole([Role.Admin.toLowerCase()]);

    /**
     * Determines if the user has a role that matches one of the provided roles.
     * @param roles an array of roles to check.
     */
    inRole(roles: string[] = []): boolean {
        const userRole = this.userRole;
        if (userRole === null) return false;
        return (
            roles.length == 0 ||
            roles.map((role) => role.toLowerCase()).includes(userRole)
        );
    }

    /**
     * Checks if the user has permissions for a particular assignment request or bill by id.
     * @param levels A collection of permission levels.
     * @param id A request or bill id.
     */
    hasPermissions(levels: Level[], id: string | number) {
        const msalRole = this.userRole;
        if (!msalRole) return false;

        if (this.checkRole(Role.Admin, msalRole)) return true;

        const isExec = this.checkRole(Role.Executive, msalRole);
        const isExecOrManager =
            isExec || this.checkRole(Role.Manager, msalRole);
        for (const level of levels) {
            if (msalRole === level) return true;
            switch (level) {
                case Level.Manager:
                    return isExec;
                case Level.Supervisor:
                    return isExecOrManager;
                case Level.BillTeam:
                    if (isExec) return true;
                    return this.checkAssignments(
                        (assignment: Assignment) =>
                            assignment.request.billId === id,
                    );
                case Level.AnalysisTeam:
                    if (isExec) return true;
                    return this.checkAssignments(
                        (assignment: Assignment) => assignment.request.id == id,
                    );
            }
        }
        return false;
    }

    /**
     * Checks an LAS Role type against the string value of the MSAL role.
     * @param role An LAS role.
     * @param msalRole String value of an MSAL role.
     */
    private checkRole = (role: Role, msalRole: string | null) =>
        role.valueOf().toLowerCase() === msalRole;

    /**
     * Checks if any user assignments meet some condition.
     * @param check A function to check assignments against.
     */
    private checkAssignments = (check: (assignment: Assignment) => boolean) =>
        this.user.assigments.some(check);

    /**
     * Observes msal's inProgress$ for interactive login to complete. If authenticated, attempts to authorize the user
     * with the api. Returns true if the user is authenticated and authorized; false, otherwise.
     * @private
     */
    private interactiveAuth() {
        return this.broadcast.inProgress$.pipe(
            filter(
                (status: InteractionStatus) => status == InteractionStatus.None,
            ),
            switchMap(() =>
                this.hasAccounts() ? this.authorize() : of(false),
            ),
            tap((result) => this.handleAuth(result)),
            catchError((error) => {
                console.error(error);
                this.handleAuth(false);
                return of(false);
            }),
        );
    }

    /**
     * Sets the msal active account and las user and notifies authSuccess$ observers.
     * @param isFullyAuthed is the user authenticated & authorized?
     * @private
     */
    private handleAuth(isFullyAuthed: boolean) {
        this.activeAccount = isFullyAuthed ? this.accounts[0] : null;
        if (!isFullyAuthed) this.user = null;
        this.authSuccess$.next(isFullyAuthed);
    }

    /**
     * Returns true if msal has any cached accounts; false, otherwise.
     */
    private hasAccounts = () => this.accounts.length > 0;

    /**
     * Returns the dashboard the current url is the root or login; otherwise, the current redirect url.
     * @param redirectUrl the current redirect url.
     * @private
     */
    private correctRedirect(redirectUrl: string) {
        return ['', '/', 'login'].some((route) => route === redirectUrl)
            ? '/dashboard'
            : redirectUrl;
    }

    private getUserAssignments() {
        const requestsUrl = this.router.getApiUrl('requests');
        const url = `${requestsUrl}/user/all`;
        return this.http.get<Assignment[]>(url);
    }

    ngOnDestroy(): void {
        this.authSuccess$.next(false);
        this.authSuccess$.complete();
    }
}
